import type { HealthRow } from './grouping';

/**
 * Column totals and per-row share for the Inventory health grid.
 *
 * Pure functions, kept out of the view for the same reason grouping.ts is — the tree walk
 * below has an easy off-by-one-level failure mode (children measured against the page total
 * rather than their own parent) that reads as plausible numbers, so it needs direct tests.
 */

/** Columns whose values ADD UP. Everything else is a ratio, a label, or an affordance. */
const SUMMABLE_KEYS = [
    'currentStock',
    // The moving part of currentStock (already inside it), so the totals row can print the
    // shelf part (currentStock − stockTransfer) the column shows.
    'stockTransfer',
    'stockInternal',
    'stockAtManufacturer',
    'inTransitInternal',
    'inTransitAtManufacturer',
    'reorderPoint',
    'shortfall',
    'drr',
    // The rate the thresholds were sized on. Additive for the same reason `drr` is — it is
    // units per day — and it belongs in the totals because `reorderPoint` above is summed:
    // without it the totals row shows a threshold with no visible rate behind it.
    'ropDrr',
    'fgDeficit',
    'rmRequired',
] as const;

/** The two inbound columns live under `row.inbound`, so they cannot be read by key. */
const INBOUND_KEYS = ['inTransit', 'open'] as const;

export type HealthTotals = {
    /** Column key → summed value, for every additive column. */
    sums: Record<string, number>;
    /** Recomputed from the totals, never averaged across rows. */
    coverDays: number | null;
    /**
     * The page's demand move, recomputed from summed current and summed prior rates — the same
     * rule a group row uses, for the same reason: averaging each row's percentage would let a
     * SKU selling two units a day swing the headline as hard as one selling two thousand.
     */
    drrDeltaPct: number | null;
    rowCount: number;
};

/**
 * Sum the additive columns over the rows the grid is CURRENTLY showing.
 *
 * Pass the top-level rows only. Grouped or flat makes no difference to the answer — a group
 * row is already the sum of its children, so both shapes total to the same figure — which is
 * what lets the totals row stay put while the operator changes grouping.
 */
export function computeTotals(rows: HealthRow[]): HealthTotals {
    const sums: Record<string, number> = {};
    for (const key of SUMMABLE_KEYS) sums[key] = 0;
    for (const key of INBOUND_KEYS) sums[key] = 0;

    // Kept out of `sums` deliberately: it is the DENOMINATOR of the delta below, not a column
    // the grid shows, and putting it in the totals record would offer it to the aggregate row
    // as though there were a "previous DRR" column to print it under.
    let previousDrr = 0;

    for (const row of rows) {
        for (const key of SUMMABLE_KEYS) {
            sums[key] += Number(row[key] ?? 0) || 0;
        }
        for (const key of INBOUND_KEYS) {
            sums[key] += Number(row.inbound?.[key] ?? 0) || 0;
        }
        previousDrr += Number(row.drrPrevious ?? 0) || 0;
    }

    return {
        sums,
        // On-hand over run rate, matching how a group row derives its own cover. Averaging
        // the rows' cover-days instead would describe no row and no page.
        coverDays:
            sums.drr > 0 ? Number((sums.currentStock / sums.drr).toFixed(1)) : null,
        drrDeltaPct:
            previousDrr > 0
                ? Number((((sums.drr - previousDrr) / previousDrr) * 100).toFixed(1))
                : null,
        rowCount: rows.length,
    };
}

/**
 * The magnitude a row's share is measured on.
 *
 * Finished goods use DRR: "what fraction of everything we sell in a day is this SKU" is the
 * question a contribution column is actually asked, and it is the one figure on the row that
 * describes demand rather than position.
 *
 * Materials have no demand of their own — their DRR is 0 on every row by construction (the
 * need is exploded from the FG plan through the BOM), so a DRR share would be 0/0 for the
 * whole grid. `rmRequired` IS the material's demand signal, so it stands in.
 */
export function contributionMetricOf(row: HealthRow, isMaterial: boolean): number {
    return Number((isMaterial ? row.rmRequired : row.drr) ?? 0) || 0;
}

/**
 * Each row's share of ITS OWN PARENT, keyed by rowKey.
 *
 * Top-level rows are measured against the page total; the rows revealed inside a group are
 * measured against that group's total, because that is what they roll into. Measuring
 * children against the page instead would make an expanded group's shares sum to the
 * group's own share rather than to 100%, which is the opposite of what the column claims.
 *
 * Returns a fraction (0–1), or null where the denominator is 0 — a share of nothing is
 * undefined, not 0%, and printing 0% would assert the row contributes nothing when in fact
 * nothing contributes anything.
 */
export function computeContributions(
    topLevelRows: HealthRow[],
    isMaterial: boolean,
): Map<string, number | null> {
    const shares = new Map<string, number | null>();

    const walk = (rows: HealthRow[], denominator: number): void => {
        for (const row of rows) {
            const value = contributionMetricOf(row, isMaterial);
            shares.set(row.rowKey, denominator > 0 ? value / denominator : null);
            // A group's children are measured against the group, one level at a time, so a
            // second grouping level nests correctly without a special case.
            if (row.subRows?.length) walk(row.subRows, value);
        }
    };

    const total = topLevelRows.reduce(
        (sum, row) => sum + contributionMetricOf(row, isMaterial),
        0,
    );
    walk(topLevelRows, total);

    return shares;
}

/** `0.0731` → `7.3%`. Null (no denominator) reads as an em dash, not as 0%. */
export function formatShare(share: number | null | undefined): string {
    if (share == null) return '—';
    // Below a tenth of a percent, "0.0%" reads as "none" for a row that does contribute.
    if (share > 0 && share < 0.001) return '<0.1%';
    return `${(share * 100).toFixed(1)}%`;
}
